
<template>
    <div class="token-trades" style="max-height: 400px; overflow-y: auto;">
        <div v-if="isLoading" class="text-center p-3">Loading trades...</div>
        <div v-else-if="error" class="text-center text-danger p-3">{{ error }}</div>
        <table v-else class="table table-sm table-hover mb-0 small">
            <thead>
            <tr>
                <th>Time</th>
                <th>Exchange</th>
                <th class="text-center">Side</th>
                <th class="text-end">Price</th>
                <th>Trade</th>
                <th class="text-end">Value</th>
            </tr>
            </thead>
            <transition-group tag="tbody" name="trade">
            <tr v-for="trade in trades" :key="trade.signature + trade.instruction_index" :class="trade.is_buy ? 'text-success' : 'text-danger'">
                <td :title="getUtcString(trade.time)">
                    <a :href="`https://solscan.io/tx/${trade.signature}`" target="_blank" class="text-muted text-decoration-none">
                        {{ formatTime(trade.time) }}
                    </a>
                </td>
                <td class="exchange-cell">{{ trade.exchange || 'Unknown' }}</td>
                <td class="text-center">{{ trade.is_buy ? 'Buy' : 'Sell' }}</td>
                <td class="text-end">${{ formatPrice(trade.price_usd) }}</td>
                <td class="trade-pair">
                    <span class="trade-amount">{{ formatAmount(trade.trade_token.amount) }} {{ trade.trade_token.symbol }}</span>
                    <span class="trade-arrow">{{ trade.is_buy ? '←' : '→' }}</span>
                    <span class="pair-amount">{{ formatAmount(trade.pair_token.amount) }} {{ trade.pair_token.symbol }}</span>
                </td>
                <td class="text-end">${{ formatPrice(trade.total_value_usd) }} {{ getTradeSizeEmoji(trade.total_value_usd) }}</td>
            </tr>
            </transition-group>
            <tbody v-if="trades.length === 0">
                <td colspan="6" class="text-center text-muted py-3">No recent trades found.</td>
            </tbody>
        </table>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

const props = defineProps({
    mint: {
        type: String,
        required: true,
    }
});

const trades = ref([]);
const isLoading = ref(false);
const error = ref(null);
let intervalId = null;

// --- Formatting Helpers ---
// Display relative time (e.g., "5m ago")
const formatTime = (timestamp) => {
    // Expecting timestamp as an ISO 8601 string like "2025-03-27T15:00:15.944Z"
    if (typeof timestamp !== 'string') return 'Invalid Date';

    const now = Date.now();
    const tradeTime = new Date(timestamp).getTime(); // Parse ISO string to milliseconds

    // Check if the resulting date is valid
    if (isNaN(tradeTime)) return 'Invalid Date';

    const diffSeconds = Math.round((now - tradeTime) / 1000);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.round(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays}d ago`;
};

// Get full UTC string for title attribute (hover)
const getUtcString = (timestamp) => {
    // Expecting timestamp as an ISO 8601 string like "2025-03-27T15:00:15.944Z"
    // It's already UTC, just return it if it's a valid string
    return typeof timestamp === 'string' ? timestamp : 'Invalid Date';
};

const formatPrice = (price) => {
    if (price === 0 || !price) return '0.00';
    if (price > 1) return price.toFixed(2);
    if (price > 0.01) return price.toFixed(4);
    return price.toPrecision(3);
};

const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return amount.toLocaleString(undefined, { maximumFractionDigits: 4 }); // Adjust digits as needed
};

// Get emoji based on trade value in USD
const getTradeSizeEmoji = (totalValueUsd) => {
    if (totalValueUsd < 250) return '🦐';
    if (totalValueUsd < 2000) return '🐬';
    return '🐳';
};
// --- End Formatting Helpers ---


const fetchTrades = async () => {
    // Don't set isLoading to true on interval refresh to avoid flicker
    // isLoading.value = true;
    error.value = null;
    try {
        const response = await axios.get(`/api/tokens/${props.mint}/trades`, { params: { limit: 30 } }); // Fetch last 30 trades
        // Simple update: replace all trades. Could be smarter (prepend new ones)
        trades.value = response.data;
    } catch (err) {
        console.error('Error fetching token trades:', err);
        error.value = 'Failed to load trades.';
        // Don't clear trades on error, keep showing old ones
    } finally {
        // isLoading.value = false;
    }
};

onMounted(() => {
    isLoading.value = true; // Set loading only on initial mount
    fetchTrades().finally(() => isLoading.value = false);
    intervalId = setInterval(fetchTrades, 5000); // Refresh trades every 5 seconds
});

onUnmounted(() => {
    clearInterval(intervalId);
});
</script>

<style scoped>
/* Compact table styling */
.table td, .table th {
    padding: 0.3rem 0.5rem;
    vertical-align: middle;
}

/* Exchange column styling */
.exchange-cell {
    font-size: 0.75rem;
    opacity: 0.85;
}

/* Trade pair display */
.trade-pair {
    white-space: nowrap;
}

.trade-arrow {
    margin: 0 0.25rem;
    opacity: 0.6;
}

.pair-amount {
    opacity: 0.7;
    font-size: 0.85em;
}

/* Ensure buy/sell colors work in dark mode */
.text-success {
    color: var(--compass-success, #22c55e) !important;
}

.text-danger {
    color: var(--compass-danger, #ef4444) !important;
}

/* Table dark mode styling */
:global(.dark-mode) .table {
    color: var(--compass-text-primary, #e6edf3);
}

:global(.dark-mode) .table th {
    color: var(--compass-text-secondary, #7d8590);
    border-color: var(--compass-border, rgba(139, 92, 246, 0.15));
}

:global(.dark-mode) .table td {
    border-color: var(--compass-border, rgba(139, 92, 246, 0.1));
}

:global(.dark-mode) .table-hover tbody tr:hover {
    background: rgba(139, 92, 246, 0.08);
}

:global(.dark-mode) .text-muted {
    color: var(--compass-text-secondary, #7d8590) !important;
}

/* Trade row transition */
.trade-enter-active,
.trade-leave-active {
    transition: all 0.5s ease;
}
.trade-enter-from,
.trade-leave-to {
    opacity: 0;
    transform: translateY(-15px);
}
</style>
